<template>
    <scalar-array-input-group
        v-model="kenmerken"
        :items="availableKenmerken"
        :schema="props.schema"
        value-key-name="id"
        :loading="loading"
    >
        <template #label>
            <flex-container :gap="Size.sm">
                <input-label>
                    <slot name="label">
                        Welk kenmerk uit Realworks wil je opvolgen?
                    </slot>
                </input-label>

                <span v-tooltip.info="props.tooltip">
                    <svg-icon
                        :icon="IconCircleInfo"
                        :size="IconSize.sm"
                        is-custom-icon
                    />
                </span>
            </flex-container>
        </template>

        <template #input>
            <multi-select-input
                :items="availableKenmerken"
                value-key-name="id"
                label-key-name="omschrijving"
                :placeholder="props.placeholder"
            />
        </template>
    </scalar-array-input-group>
</template>

<script setup lang="ts">
    import {
        FlexContainer,
        IconSize,
        InputLabel,
        MultiSelectInput,
        Size,
        SvgIcon,
    } from '@keyvue/one-ui';

    import { onMounted, ref, watch } from 'vue';
    import { array } from 'yup';
    import { RealworksKenmerkAPI, type RealworksKenmerk } from '@keyvue/one-api-client-legacy';
    import IconCircleInfo from '@keyvue/one-awesome-icons/icons/regular/circle-info.svg?component';
    import ScalarArrayInputGroup from '@/shared/components/ScalarArrayInputGroup.vue';

    interface Props {
        modelValue: string[];
        tooltip?: string;
        placeholder?: string;
        schema?: any;
    }

    const props = withDefaults(defineProps<Props>(), {
        tooltip: 'Welk kenmerk uit Realworks wil je opvolgen?',
        placeholder: 'Selecteer kenmerk',
        schema: () => array().min(1),
    });

    const emit = defineEmits<{
        (e: 'loading'): void;
        (e: 'loaded'): void;
        (e: 'update:modelValue', value: string[]): void;
    }>();

    const kenmerken = ref(props.modelValue);
    const availableKenmerken = ref<RealworksKenmerk[]>([]);
    const loading = ref(true);

    async function loadData() {
        loading.value = true;
        emit('loading');
        availableKenmerken.value = (await RealworksKenmerkAPI.getAll())
            .sort((a, b) => a.omschrijving.localeCompare(b.omschrijving));
        loading.value = false;
        emit('loaded');
    }

    onMounted(() => {
        loadData();
    });

    watch(kenmerken, () => {
        emit('update:modelValue', kenmerken.value);
    });

    watch(() => props.modelValue, () => {
        kenmerken.value = props.modelValue;
    });
</script>
